import React, { useCallback } from 'react';
import { CpOrderInvoicesSearchModel } from './cp_order_invoices_search_types';
import FormEdit from '../../../../core/components/form/FormEdit';
import ValidInputDroplistText from '../../../../core/components/validation/ValidInputDroplistText';
import ValidCheckbox from '../../../../core/components/validation/ValidCheckbox';
import Button from '../../../../core/components/Button';
import { useCpOrderInvoicesSearchState } from './useCpOrderInvoicesSearchState';
import { useCpOrderInvoicesSearchInit } from './useCpOrderInvoicesSearchInit';
import { useDateRangeControllerState } from '../../../../core/hooks/useDateRangeControllerState';
import DateRangeController from '../../../../core/components/DateRangeController';
import ValidInputRadioText from '../../../../core/components/validation/ValidInputRadioText';
import ValidInputMultiSelect from '../../../../core/components/validation/ValidInputMultiSelect';
import { CP_ORDER_INVOICES_DATE_RANGE_MODES } from '../../../../core/utils/dateRangeModesByFeature.const';
import utilSvc from '../../../../core/utilSvc';
import { useCpOrderInvoicesStoreActions } from '../pagehooks/useCpOrderInvoicesStore';
import { useAtomValue } from 'jotai';
import { cpOrderInvoicesPageActionAtom, cpOrderInvoicesUserTypeAtom } from '../cp_order_invoices_list_atoms';
import FormSearch from '../../../../core/components/FormSearch';
import { programAdminRenameMap } from '../pagehooks/useCpOrderInvoicesInit';

const CpOrderInvoicesListSearch: React.FC = () => {
    const action = useAtomValue(cpOrderInvoicesPageActionAtom);
    const init = useCpOrderInvoicesSearchInit();

    if (init) {
        return null;
    }
    const drawerOpen = action == 'QUERY';
    return drawerOpen ? <CpOrderInvoicesListForm /> : <CpOrderInvoicesListFormSearch />;
};

const CpOrderInvoicesListForm = (): JSX.Element => {
    const userType = useAtomValue(cpOrderInvoicesUserTypeAtom);
    const { state, setProperty, handleSearch } = useCpOrderInvoicesSearchState();
    // Date range state from global form state
    const dateRangeMode = typeof state.DateRangeMode?.val === 'string' ? state.DateRangeMode.val : 'TODAY';
    const dateRangeStart = state.StartDate?.val ? utilSvc.dt.parseLocalDate(state.StartDate.val) : new Date();
    const dateRangeEnd = state.EndDate?.val ? utilSvc.dt.parseLocalDate(state.EndDate.val) : new Date();

    const {
        monthlyMonth,
        monthlyYear,
        qtr,
        qtrYear,
        handleModeChange,
        handleStartChange,
        handleEndChange,
        handleMonthlyChange,
        handleQuarterChange,
        startError,
        endError,
    } = useDateRangeControllerState(dateRangeMode, dateRangeStart, dateRangeEnd, setProperty);

    const handleCESSelect = useCallback(
        (prop: string, val: string | number): void => {
            setProperty('ProgramAdmin', 'N/A');
            setProperty(prop as keyof CpOrderInvoicesSearchModel, val);
        },
        [setProperty],
    );

    const handleRXSelect = useCallback(
        (val: (string | number)[]): void => {
            setProperty('RX', val);
        },
        [setProperty],
    );

    const handleProgAdminSelect = useCallback(
        (prop: string, val: string | number): void => {
            setProperty('CE', 'N/A');
            setProperty(prop as keyof CpOrderInvoicesSearchModel, val);
        },
        [setProperty],
    );

    const ceProp = userType === 'CE' ? { ...state.CE, lbl: 'Facility' } : state.CE;

    return (
        <FormEdit
            formName="cpOrderInvoicesSearch"
            cssName="cp-order-invoices-search"
            fieldsetId="cp-order-invoices-search-fieldset"
        >
            <div className="fieldset-form">
                <div className="fieldset-inline">
                    <div className="fieldset-section">
                        {userType === 'CE' ? (
                            <>
                                <ValidInputDroplistText
                                    prop={ceProp}
                                    onChange={handleCESSelect}
                                />
                            </>
                        ) : (
                            <>
                                <ValidInputRadioText
                                    prop={state.ReportType}
                                    onChange={(_, val) => setProperty('ReportType', val)}
                                />
                                {state.ReportType.val === 'facility' ? (
                                    <ValidInputDroplistText
                                        prop={ceProp}
                                        onChange={handleCESSelect}
                                    />
                                ) : (
                                    <ValidInputDroplistText
                                        prop={state.ProgramAdmin}
                                        onChange={handleProgAdminSelect}
                                    />
                                )}
                            </>
                        )}
                    </div>
                    {state.ReportType.val === 'facility' && state.CE.val !== 'N/A' ? (
                        <div className="fieldset-section">
                            <ValidInputMultiSelect
                                id="cp-order-invoice-rx-select"
                                prop={state.RX}
                                onOptionSelect={handleRXSelect}
                                autoSelectSingleOption
                                labelNoMinWidth
                            />
                        </div>
                    ) : null}
                    {state.ReportType.val === 'programAdmin' &&
                    state.ProgramAdmin.val !== 'N/A' &&
                    userType !== 'CE' ? (
                        <div className="fieldset-section">
                            <ValidInputMultiSelect
                                id="cp-order-invoice-rx-select"
                                prop={state.RX}
                                onOptionSelect={handleRXSelect}
                                autoSelectSingleOption
                                labelNoMinWidth
                            />
                        </div>
                    ) : null}
                </div>
                <div className="fieldset-inline">
                    <DateRangeController
                        allowedModes={CP_ORDER_INVOICES_DATE_RANGE_MODES}
                        mode={dateRangeMode}
                        start={dateRangeStart}
                        end={dateRangeEnd}
                        monthlyMonth={monthlyMonth}
                        monthlyYear={monthlyYear}
                        qtr={qtr}
                        qtrYear={qtrYear}
                        onModeChange={handleModeChange}
                        onStartChange={handleStartChange}
                        onEndChange={handleEndChange}
                        onMonthlyChange={handleMonthlyChange}
                        onQuarterChange={handleQuarterChange}
                        startError={startError}
                        endError={endError}
                    />
                </div>
                <div className="fieldset-inline">
                    <div className="fieldset-section">
                        <ValidCheckbox
                            id="overallocated-only"
                            label="Overallocated Only"
                            checked={state.OverallocatedOnly.val}
                            onChange={val => setProperty('OverallocatedOnly', val)}
                            alignLeft
                        />
                        <ValidCheckbox
                            id="rebates-only"
                            label="Rebates Only"
                            checked={state.RebatesOnly.val}
                            onChange={val => setProperty('RebatesOnly', val)}
                            alignLeft
                        />
                        <ValidCheckbox
                            id="reconciled-only"
                            label="Show Reconciled Only"
                            checked={state.ReconciledOnly.val}
                            onChange={val => setProperty('ReconciledOnly', val)}
                            alignLeft
                        />
                    </div>
                </div>
                <div className="form-field">
                    <Button
                        onClick={handleSearch}
                        cssName="btn-submit"
                    >
                        Search
                    </Button>
                </div>
            </div>
        </FormEdit>
    );
};

const CpOrderInvoicesListFormSearch: React.FC = () => {
    const { setAction } = useCpOrderInvoicesStoreActions();
    const { state } = useCpOrderInvoicesSearchState();
    const criteria = normalizeFormCriteria(state);

    return (
        <FormSearch
            criteria={criteria}
            onEdit={() => setAction('QUERY')}
        />
    );
};

export default CpOrderInvoicesListSearch;

const normalizeFormCriteria = (form: CpOrderInvoicesSearchModel): Record<string, any> => {
    const { CE, ProgramAdmin } = form ?? {};

    let criteria = {
        ...form,
        RX: {
            ...form.RX,
            lbl: 'Pharmacy',
            val: form.RX.val?.length === 0 || form.RX.val?.length === form.RX.options?.length ? 'All' : form.RX?.val,
        },
        ReportType: { ...form.ReportType, val: form.ReportType?.val === 'facility' ? 'Facility' : 'Program Admin' },
        ProgramAdmin: {
            ...form.ProgramAdmin,
            val: programAdminRenameMap[form.ProgramAdmin.val] ?? form.ProgramAdmin.val,
        },
    };

    if (CE?.val === 'N/A' || CE?.val === '') {
        /* eslint-disable-next-line */
        const { CE, ...restCriteria } = criteria;
        criteria = restCriteria as any;
    } else {
        criteria.CE = { ...criteria.CE, lbl: 'Facility' };
    }
    if (ProgramAdmin?.val === 'N/A' || ProgramAdmin?.val === '') {
        /* eslint-disable-next-line */
        const { ProgramAdmin, ...restCriteria } = criteria;
        criteria = restCriteria as any;
    } else {
        criteria.ProgramAdmin = { ...criteria.ProgramAdmin, lbl: 'Program Admin' };
    }

    return criteria;
};
